import React, { useState, useEffect, memo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Phone, AlertTriangle, Wind, Shield, ChevronRight } from 'lucide-react';
import { Z_CLASS } from '../../../constants/zIndex';
import { cn } from '../../../shared/utils/cn';
import { haptic } from '../../../shared/utils/haptics';

// Default emergency contacts (French + international)
const DEFAULT_EMERGENCY_CONTACTS = [
  { name: 'SAMU', number: '15', icon: '🚑' },
  { name: 'SOS Amitié', number: '09 72 39 40 50', icon: '💙' },
  { name: 'Fil Santé Jeunes', number: '0 800 235 236', icon: '🌟' },
];

const STORAGE_KEY_CONTACTS = 'adhd-planner-emergency-contacts';

// Load user-defined contacts from localStorage
function loadCustomContacts() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY_CONTACTS);
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
}

// Save user-defined contacts
function saveCustomContacts(contacts) {
  localStorage.setItem(STORAGE_KEY_CONTACTS, JSON.stringify(contacts));
}

// Breathing exercises
export const BREATHING_EXERCISES = [
  {
    id: 'box',
    name: 'Respiration carrée',
    description: '4 secondes inspire, 4 tient, 4 expire, 4 tient',
    steps: [
      { action: 'Inspire', duration: 4 },
      { action: 'Retiens', duration: 4 },
      { action: 'Expire', duration: 4 },
      { action: 'Retiens', duration: 4 }
    ]
  },
  {
    id: '478',
    name: 'Technique 4-7-8',
    description: 'Calme le système nerveux rapidement',
    steps: [
      { action: 'Inspire', duration: 4 },
      { action: 'Retiens', duration: 7 },
      { action: 'Expire', duration: 8 }
    ]
  },
  {
    id: 'simple',
    name: 'Respiration simple',
    description: 'Inspire et expire lentement',
    steps: [
      { action: 'Inspire', duration: 4 },
      { action: 'Expire', duration: 6 }
    ]
  }
];

// Support affirmations
export const AFFIRMATIONS = [
  "Ce moment va passer. Tu as déjà traversé des tempêtes.",
  "Tu n'es pas seul·e. De l'aide existe.",
  "Tes émotions sont valides, mais elles ne définissent pas ta réalité.",
  "Une respiration à la fois. Tu peux le faire.",
  "Tu mérites de l'aide et du soutien.",
  "Ce que tu ressens est temporaire, pas permanent."
];

/**
 * CrisisModeOverlay - Emergency support interface
 */
export const CrisisModeOverlay = memo(function CrisisModeOverlay({ onClose, customContacts = [] }) {
  const [view, setView] = useState('main');
  const [affirmation, setAffirmation] = useState('');
  const [userContacts, setUserContacts] = useState(loadCustomContacts);
  const [editMode, setEditMode] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState('');

  useEffect(() => {
    haptic.crisis();
    setAffirmation(AFFIRMATIONS[Math.floor(Math.random() * AFFIRMATIONS.length)]);
  }, []);

  const handleClose = useCallback(() => {
    haptic.tap();
    onClose();
  }, [onClose]);

  const addContact = useCallback(() => {
    if (!newName.trim() || !newNumber.trim()) return;
    const updated = [...userContacts, { name: newName.trim(), number: newNumber.trim(), icon: '📱', custom: true }];
    setUserContacts(updated);
    saveCustomContacts(updated);
    setNewName('');
    setNewNumber('');
  }, [newName, newNumber, userContacts]);

  const removeContact = useCallback((idx) => {
    const updated = userContacts.filter((_, i) => i !== idx);
    setUserContacts(updated);
    saveCustomContacts(updated);
  }, [userContacts]);

  const contacts = [...userContacts, ...customContacts, ...DEFAULT_EMERGENCY_CONTACTS];

  return (
    <motion.div
      className={cn('fixed inset-0 bg-[var(--bg-base,#09090b)] flex flex-col', Z_CLASS.CRISIS_MODE)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[10%] left-[10%] w-[300px] h-[300px] bg-zinc-800/30 rounded-full blur-[100px]" />
        <div className="absolute bottom-[20%] right-[5%] w-[200px] h-[200px] bg-zinc-800/20 rounded-full blur-[80px]" />
      </div>

      {/* Header */}
      <motion.div
        className="relative p-4 flex items-center justify-between border-b border-white/[0.06]"
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.1 }}
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 glass-subtle rounded-lg flex items-center justify-center">
            <Shield className="w-4 h-4 text-zinc-500" />
          </div>
          <span className="font-medium text-sm text-zinc-100">Mode Sécurité</span>
        </div>
        <motion.button
          onClick={handleClose}
          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <X className="w-5 h-5 text-zinc-400" />
        </motion.button>
      </motion.div>

      {/* Main content */}
      <div className="relative flex-grow flex flex-col items-center justify-center p-6">
        <AnimatePresence mode="wait">
          {view === 'main' && (
            <MainView
              key="main"
              affirmation={affirmation}
              onBreathing={() => { haptic.tap(); setView('breathing'); }}
              onContacts={() => { haptic.tap(); setView('contacts'); }}
            />
          )}
          {view === 'breathing' && (
            <BreathingView key="breathing" onBack={() => { haptic.tap(); setView('main'); }} />
          )}
          {view === 'contacts' && (
            <ContactsView key="contacts" contacts={contacts} onBack={() => { haptic.tap(); setView('main'); }}
              editMode={editMode} onToggleEdit={() => setEditMode(p => !p)}
              onAddContact={addContact} onRemoveContact={removeContact}
              newName={newName} newNumber={newNumber}
              onNameChange={setNewName} onNumberChange={setNewNumber}
            />
          )}
        </AnimatePresence>
      </div>

      {/* Footer */}
      <motion.div
        className="relative p-4 text-center border-t border-white/[0.06]"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        <p className="text-zinc-600 text-[10px]">Tu es important·e. Cette application est là pour t'aider.</p>
      </motion.div>
    </motion.div>
  );
});

// Main view component
const MainView = memo(function MainView({ affirmation, onBreathing, onContacts }) {
  return (
    <motion.div
      className="text-center space-y-8"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <motion.div
        className="w-16 h-16 mx-auto glass-card flex items-center justify-center"
        animate={{ scale: [1, 1.05, 1] }}
        transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
      >
        <Heart className="w-8 h-8 text-zinc-500" />
      </motion.div>

      <motion.p
        className="text-zinc-200 text-base font-medium max-w-xs mx-auto leading-relaxed"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
      >
        {affirmation}
      </motion.p>

      <motion.div
        className="space-y-2 w-full max-w-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <motion.button
          onClick={onBreathing}
          className="w-full flex items-center gap-3 p-4 glass-interactive text-left"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
        >
          <Wind className="w-5 h-5 text-zinc-500" />
          <span className="flex-1 text-zinc-200 font-medium text-sm">Exercice de respiration</span>
          <ChevronRight className="w-4 h-4 text-zinc-600" />
        </motion.button>

        <motion.button
          onClick={onContacts}
          className="w-full flex items-center gap-3 p-4 glass-interactive text-left"
          whileHover={{ x: 4 }}
          whileTap={{ scale: 0.98 }}
        >
          <Phone className="w-5 h-5 text-zinc-500" />
          <span className="flex-1 text-zinc-200 font-medium text-sm">Appeler quelqu'un</span>
          <ChevronRight className="w-4 h-4 text-zinc-600" />
        </motion.button>
      </motion.div>

      <motion.div
        className="flex items-center justify-center gap-2 text-zinc-500 text-xs"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <AlertTriangle className="w-3.5 h-3.5" />
        <span>En urgence vitale, appelle le 15</span>
      </motion.div>
    </motion.div>
  );
});

// Breathing exercise view
const BreathingView = memo(function BreathingView({ onBack }) {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [cycles, setCycles] = useState(0);

  useEffect(() => {
    if (!isRunning || !selectedExercise) return;

    const step = selectedExercise.steps[currentStep];
    setCountdown(step.duration);

    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          const nextStep = (currentStep + 1) % selectedExercise.steps.length;
          setCurrentStep(nextStep);
          if (nextStep === 0) setCycles(c => c + 1);
          return selectedExercise.steps[nextStep].duration;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isRunning, selectedExercise, currentStep]);

  const handleStart = useCallback((exercise) => {
    haptic.tap();
    setSelectedExercise(exercise);
    setCurrentStep(0);
    setCycles(0);
    setIsRunning(true);
  }, []);

  const handleStop = useCallback(() => {
    haptic.tap();
    setIsRunning(false);
    setSelectedExercise(null);
  }, []);

  if (isRunning && selectedExercise) {
    const step = selectedExercise.steps[currentStep];
    const isInhale = step.action === 'Inspire';
    const isExhale = step.action === 'Expire';

    return (
      <motion.div
        className="text-center space-y-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.button onClick={handleStop} className="text-zinc-400 text-sm hover:text-white transition-colors" whileHover={{ x: -4 }}>
          ← Arrêter
        </motion.button>

        <motion.div
          className="relative w-40 h-40 mx-auto rounded-full border-4 border-zinc-700 flex items-center justify-center"
          animate={{
            scale: isInhale ? 1.2 : isExhale ? 0.9 : 1,
            borderColor: isInhale ? '#3b82f6' : isExhale ? '#10b981' : '#71717a'
          }}
          transition={{ duration: step.duration, ease: 'easeInOut' }}
        >
          <div className="text-center">
            <motion.span className="block text-lg font-medium text-zinc-200" key={step.action} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              {step.action}
            </motion.span>
            <span className="block text-3xl font-bold text-zinc-100 mt-1">{countdown}</span>
          </div>
        </motion.div>

        <p className="text-zinc-400 text-sm">Cycle {cycles + 1}</p>

        <div className="flex justify-center gap-2">
          {selectedExercise.steps.map((_, i) => (
            <motion.div key={i} className={cn('w-2 h-2 rounded-full', i === currentStep ? 'bg-blue-400' : 'bg-zinc-700')} animate={{ scale: i === currentStep ? 1.3 : 1 }} />
          ))}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div className="space-y-6 w-full max-w-xs" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <motion.button onClick={onBack} className="text-zinc-500 text-xs hover:text-zinc-300 transition-colors" whileHover={{ x: -4 }}>← Retour</motion.button>
      <h2 className="text-zinc-100 text-base font-medium text-center">Choisis un exercice</h2>
      <div className="space-y-2">
        {BREATHING_EXERCISES.map((exercise, index) => (
          <motion.button
            key={exercise.id}
            onClick={() => handleStart(exercise)}
            className="w-full p-4 glass-interactive text-left"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ x: 4 }}
            whileTap={{ scale: 0.98 }}
          >
            <span className="block font-medium text-sm text-zinc-200">{exercise.name}</span>
            <span className="block text-xs text-zinc-500 mt-1">{exercise.description}</span>
          </motion.button>
        ))}
      </div>
    </motion.div>
  );
});

// Contacts view with add/remove support
const ContactsView = memo(function ContactsView({ contacts, onBack, editMode, onToggleEdit, onAddContact, onRemoveContact, newName, newNumber, onNameChange, onNumberChange }) {
  return (
    <motion.div className="space-y-4 w-full max-w-xs" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
      <div className="flex items-center justify-between">
        <motion.button onClick={onBack} className="text-zinc-500 text-xs hover:text-zinc-300 transition-colors" whileHover={{ x: -4 }}>← Retour</motion.button>
        <button onClick={onToggleEdit} className="text-[10px] text-zinc-600 hover:text-zinc-300">
          {editMode ? 'Terminer' : 'Modifier'}
        </button>
      </div>
      <h2 className="text-zinc-100 text-base font-medium text-center">Contacts d'aide</h2>
      <div className="space-y-2">
        {contacts.map((contact, i) => (
          <motion.div
            key={i}
            className="flex items-center gap-3 p-3 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
          >
            <a href={`tel:${contact.number.replace(/\s/g, '')}`} className="flex items-center gap-3 flex-1 min-w-0">
              <span className="text-lg">{contact.icon || '📞'}</span>
              <div className="flex-1 min-w-0">
                <span className="block text-sm font-medium text-zinc-200 truncate">{contact.name}</span>
                <span className="block text-xs text-zinc-500">{contact.number}</span>
              </div>
              <Phone className="w-4 h-4 text-zinc-500 shrink-0" />
            </a>
            {editMode && contact.custom && (
              <button onClick={() => onRemoveContact(i)} className="text-red-400 text-xs px-2 py-1 shrink-0">✕</button>
            )}
          </motion.div>
        ))}
      </div>

      {/* Add contact form */}
      {editMode && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-2 pt-2">
          <input
            type="text"
            value={newName}
            onChange={e => onNameChange(e.target.value)}
            placeholder="Nom du contact"
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600"
          />
          <input
            type="tel"
            value={newNumber}
            onChange={e => onNumberChange(e.target.value)}
            placeholder="Numero de telephone"
            className="w-full px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800 text-sm text-white placeholder-zinc-600"
          />
          <button
            onClick={onAddContact}
            disabled={!newName.trim() || !newNumber.trim()}
            className="w-full py-2 rounded-lg bg-blue-500/20 text-blue-300 text-sm font-medium disabled:opacity-30"
          >
            + Ajouter
          </button>
        </motion.div>
      )}

      <p className="text-center text-zinc-600 text-[10px]">Appuie sur un contact pour appeler</p>
    </motion.div>
  );
});

/**
 * CrisisModeButton - Quick access button
 */
export const CrisisModeButton = memo(function CrisisModeButton({ onClick }) {
  const handleClick = useCallback(() => {
    haptic.tap();
    onClick();
  }, [onClick]);

  return (
    <motion.button
      onClick={handleClick}
      className="p-2 glass-card text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
      title="Mode Sécurité"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <Shield className="w-4 h-4" />
    </motion.button>
  );
});

export default CrisisModeOverlay;
